import { useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAtomValue } from "jotai";
import { Input } from "@common/Form/Input";
import { Button } from "@common/Button";
import { ComponentColor, ComponentSize, LabelPlacement } from "@common/types";
import { useApi } from "@/hooks/apiHooks/useApi";
import { useCategoryApi } from "@/hooks/apiHooks/useCategoryApi";
import { isProcessingCategoriesAtom } from "@/store/categoryAtoms";
import type { CategoryResponse } from "@models/category";
import type { GetAllProductsResponse } from "@models/product";
import { GetProductsSortOptionType } from "@models/productFilter";

// The picker lists one page of the category's active products, by name.
const PICKER_PAGE_SIZE = 100;

const DIALOG_ID = "editCategoryModal";

// Located by id rather than a ref, as NewCategory does: the submit handler
// runs through handleSubmit during render, and a ref read from it trips
// react-hooks/refs.
const editDialogElement = (): HTMLDialogElement | null => {
    const element = document.getElementById(DIALOG_ID);
    return element instanceof HTMLDialogElement ? element : null;
};

// Strings-in-form (matches NewCategory / the product form); parsed on submit.
const editCategorySchema = z.object({
    // Not `name`: Form/Input uses the field name as the element id, and the
    // always-mounted New Category dialog already owns id="name".
    // Trimmed BEFORE the check, as submit trims: a lone space must not pass
    // validation and PUT an empty name.
    categoryName: z.string().trim().nonempty("Category name is required"),
    // Blank = not pinned (null). Same rule as the product form's rank.
    popularRank: z
        .string()
        .refine((val) => !val || /^[1-9]\d*$/.test(val.trim()), {
            message: "Popular Rank must be a positive integer",
        }),
    // "" = None (null); otherwise a product id from the picker.
    featuredProductId: z.string(),
});

type EditCategoryFormData = z.infer<typeof editCategorySchema>;

interface PickerOption {
    id: string;
    name: string;
}

// The picker's two reads (the current pin, the product page) are issued on
// mount, so there is no idle instant. `ready` is published once BOTH have
// settled, whatever each concluded; the registered <select> mounts only then,
// so a save can never send a value the picker has not shown.
type Picker =
    | { status: "loading" }
    | {
        status: "ready";
        options: PickerOption[];
        // Active products filed directly under the category — the page is
        // capped, and the cap is declared to the admin when it bites.
        productCount: number;
        // The page loaded, uncapped, with no product filed directly under the
        // category: there is nothing it can feature.
        noOwnProducts: boolean;
        productsUnavailable: boolean;
        pinUnavailable: boolean;
    };

interface EditCategoryProps {
    category: CategoryResponse;
    // Carried through unchanged — the update body is full-replace and this
    // dialog does not reparent (see useCategoryApi.updateCategory).
    parentId: number | null;
    onClose: () => void;
    // Called after this dialog has closed, to open the delete confirm (I4).
    onDelete: () => void;
}

// Mounted per category (the parent keys it on the id) and opened on mount,
// so the form's defaultValues are the category being edited. Closing goes
// through dialog.close() (Save, Cancel, backdrop, Escape alike) so the
// native close steps — focus restore included — run before onClose unmounts.
export const EditCategory = ({
    category,
    parentId,
    onClose,
    onDelete,
}: EditCategoryProps) => {
    const { callApi } = useApi();
    const { updateCategory, getFeaturedProduct } = useCategoryApi();
    const isProcessing = useAtomValue(isProcessingCategoriesAtom);
    const [picker, setPicker] = useState<Picker>({ status: "loading" });

    const methods = useForm<EditCategoryFormData>({
        resolver: zodResolver(editCategorySchema),
        defaultValues: {
            categoryName: category.name,
            popularRank:
                category.popularRank != null
                    ? String(category.popularRank)
                    : "",
            // Pre-fills only while the pin read is in flight — the server
            // answer below replaces it before the picker mounts.
            featuredProductId: category.featuredProductId ?? "",
        },
    });
    const { setValue } = methods;

    useEffect(() => {
        editDialogElement()?.showModal();
    }, []);

    useEffect(() => {
        const cachedPinId = category.featuredProductId ?? "";

        const load = async () => {
            const [pin, page] = await Promise.allSettled([
                getFeaturedProduct(category.id),
                // A category features one of ITS OWN active products, filed
                // directly under it (the server 412s anything else), so the
                // picker asks for exactly that set: without subcategories,
                // which categoryIds would otherwise expand to (BR-03). The
                // server pages and counts that set. A plain read:
                // useProductApi.getProducts would overwrite the global
                // product-list atoms behind the admin Products page.
                callApi<GetAllProductsResponse>(
                    `/products?pageNumber=1&pageSize=${PICKER_PAGE_SIZE}&categoryIds=${category.id}&includeSubcategories=false&sortBy=${GetProductsSortOptionType.Name}`,
                ),
            ]);

            // A 2xx without a JSON body resolves a string (callApi hands it
            // back unchecked); it is a failed page read, not a page of nothing.
            const pageLoaded =
                page.status === "fulfilled" && Array.isArray(page.value?.items);
            const options: PickerOption[] =
                pageLoaded && page.status === "fulfilled"
                    ? page.value.items
                        // TODO(backend): remove once includeSubcategories=false is deployed.
                        // An older API ignores the unknown key and answers the whole
                        // subtree; this second pass keeps a subcategory's product (which
                        // the save would 412) off the list until then. Its count and its
                        // cap then cover the subtree, so the note below overstates N.
                        .filter((item) => item.categoryId === category.id)
                        .map(({ id, name }) => ({ id, name }))
                    : [];
            const productCount =
                pageLoaded && page.status === "fulfilled" ? page.value.count : 0;
            // Uncapped: the page is the whole set, so an empty one is a fact.
            const noOwnProducts =
                pageLoaded &&
                productCount <= PICKER_PAGE_SIZE &&
                options.length === 0;

            // The server is the source of truth for the current pin: 200 is
            // that product (listed first when the page does not carry it),
            // 404 is None — whatever the cached tree said. Only a read that
            // could not answer at all falls back to the cached id, kept
            // selectable under a placeholder name so the admin can still
            // keep it, clear it, or pick another and save.
            let pinId = "";
            if (pin.status === "fulfilled") {
                if (pin.value) {
                    pinId = pin.value.id;
                    if (!options.some((option) => option.id === pinId)) {
                        options.unshift({ id: pinId, name: pin.value.name });
                    }
                }
            } else if (cachedPinId) {
                pinId = cachedPinId;
                // The page may already list it under its real name; only an
                // off-page id needs the placeholder (a second option with
                // the same value would also collide as a React key).
                if (!options.some((option) => option.id === cachedPinId)) {
                    options.unshift({
                        id: cachedPinId,
                        name: "(current pin unavailable)",
                    });
                }
            }

            // Written before the select mounts, so it is what the select shows.
            setValue("featuredProductId", pinId);
            setPicker({
                status: "ready",
                options,
                productCount,
                noOwnProducts,
                productsUnavailable: !pageLoaded,
                pinUnavailable: pin.status === "rejected",
            });
        };

        // Never a floating promise: anything that escapes the two reads'
        // own settlement lands on the designed unavailable cell, not on
        // "Loading products…" with Save disabled forever.
        load().catch(() => {
            setPicker({
                status: "ready",
                options: [],
                productCount: 0,
                noOwnProducts: false,
                productsUnavailable: true,
                pinUnavailable: true,
            });
        });
    }, [
        category.id,
        category.featuredProductId,
        callApi,
        getFeaturedProduct,
        setValue,
    ]);

    const onSubmit = async (data: EditCategoryFormData) => {
        try {
            // Always all four: the server full-replaces, so an omitted
            // popularRank or featuredProductId would clear it.
            await updateCategory(category.id, {
                name: data.categoryName.trim(),
                parentId,
                popularRank: data.popularRank.trim()
                    ? Number(data.popularRank)
                    : null,
                featuredProductId: data.featuredProductId || null,
            });
            editDialogElement()?.close();
        } catch {
            // useApi already toasted (e.g. 412: the product is no longer an
            // active product of this category); keep the dialog open.
        }
    };

    const selectClass =
        "select select-bordered select-primary w-full bg-white text-neutral";

    return (
        <dialog
            id={DIALOG_ID}
            className="modal text-neutral"
            onCancel={(e) => {
                if (isProcessing) {
                    e.preventDefault();
                }
            }}
            onClose={onClose}
        >
            <div className="modal-box max-w-2xl">
                <h1 className="font-bold uppercase text-3xl">Edit Category</h1>
                <div className="divider divider-info mt-0"></div>
                <FormProvider {...methods}>
                    <form onSubmit={methods.handleSubmit(onSubmit)}>
                        <div className="space-y-4">
                            <Input
                                name="categoryName"
                                placeholder="Category Name"
                                size={ComponentSize.SM}
                                labelPlacement={LabelPlacement.Inside}
                                labelText="Category Name"
                                fullWidth={true}
                                inputClass="text-black"
                            />
                            <Input
                                name="popularRank"
                                type="number"
                                placeholder="Leave blank to unpin"
                                size={ComponentSize.SM}
                                labelPlacement={LabelPlacement.Inside}
                                labelText="Popular Rank"
                                fullWidth={true}
                                inputClass="text-black"
                            />
                            <div>
                                <span className="font-semibold block mb-1">
                                    Featured product
                                </span>
                                {picker.status === "ready" ? (
                                    <select
                                        className={selectClass}
                                        aria-label="Featured product"
                                        {...methods.register(
                                            "featuredProductId",
                                        )}
                                    >
                                        <option value="">None</option>
                                        {picker.options.map((option) => (
                                            <option
                                                key={option.id}
                                                value={option.id}
                                            >
                                                {option.name}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    // Not registered on purpose — see Picker.
                                    <select
                                        className={selectClass}
                                        aria-label="Featured product"
                                        disabled
                                    >
                                        <option value="">
                                            Loading products...
                                        </option>
                                    </select>
                                )}
                                {picker.status === "ready" &&
                                    picker.pinUnavailable && (
                                    <p className="text-red-500 text-sm mt-1">
                                        Couldn&apos;t confirm the current
                                        featured product; the selection shown
                                        comes from the cached tree.
                                    </p>
                                )}
                                {picker.status === "ready" &&
                                    picker.productsUnavailable && (
                                    <p className="text-red-500 text-sm mt-1">
                                        Couldn&apos;t load this category&apos;s
                                        products.
                                    </p>
                                )}
                                {picker.status === "ready" &&
                                    picker.noOwnProducts && (
                                    <p className="text-sm text-gray-500 mt-1">
                                        No products are filed directly under
                                        this category.
                                    </p>
                                )}
                                {picker.status === "ready" &&
                                    picker.productCount > PICKER_PAGE_SIZE && (
                                    // TODO(post-MVP): server-side search for the picker — the
                                    // page is capped at PICKER_PAGE_SIZE by name.
                                    <p className="text-xs text-gray-500 mt-1">
                                        Showing the first {PICKER_PAGE_SIZE} of{" "}
                                        {picker.productCount}
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="modal-action">
                            {/* The mockup puts Delete beside the category's
                                details. This native modal closes first (focus
                                back to the tree row) and the page opens the
                                confirm: a trap dialog nested inside it would
                                have Escape close both. Unsaved edits are
                                discarded. */}
                            <Button
                                color={ComponentColor.Error}
                                outline
                                className="mr-auto"
                                disabled={isProcessing}
                                onClick={() => {
                                    editDialogElement()?.close();
                                    onDelete();
                                }}
                            >
                                Delete Category
                            </Button>
                            <Button
                                color={ComponentColor.Primary}
                                type="submit"
                                loading={isProcessing}
                                // The picker has not shown a value yet, so
                                // there is nothing honest to save. `loading`
                                // holds it during the PUT on its own.
                                disabled={picker.status === "loading"}
                            >
                                {isProcessing ? "Saving..." : "Save"}
                            </Button>
                            <Button
                                color={ComponentColor.Neutral}
                                loading={isProcessing}
                                onClick={() => editDialogElement()?.close()}
                            >
                                Cancel
                            </Button>
                        </div>
                    </form>
                </FormProvider>
            </div>
            {!isProcessing && (
                // method="dialog" closes natively; the close event fires onClose.
                <form method="dialog" className="modal-backdrop">
                    <button>close</button>
                </form>
            )}
        </dialog>
    );
};
